// Client-side encryption utilities
// Uses Web Crypto API for AES-GCM encryption
//
// Key derivation strategy (persistent across sessions):
// - We derive the AES-GCM encryption key deterministically from the user's
//   email via PBKDF2(SHA-256, 100k iterations, fixed app salt).
// - The email is stored in localStorage (alongside the JWT token) so that
//   returning users always regenerate the SAME key and can decrypt their
//   previously stored downloads.
// - We do NOT generate random keys anymore. Random keys caused the
//   "Unable to decrypt" bug whenever the browser/session was closed.

const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const USER_EMAIL_STORAGE_KEY = 'selah_user_email';
const APP_SALT = 'selah-downloads-salt-v1';

export class EncryptionService {
  constructor() {
    this.encoder = new TextEncoder();
    this.decoder = new TextDecoder();
  }

  // Derive AES-GCM key from password/email using PBKDF2
  async deriveKey(password) {
    const passwordBuffer = this.encoder.encode(password);
    const salt = this.encoder.encode(APP_SALT);

    const importedKey = await window.crypto.subtle.importKey(
      'raw',
      passwordBuffer,
      { name: 'PBKDF2' },
      false,
      ['deriveKey']
    );

    return await window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 100000,
        hash: 'SHA-256',
      },
      importedKey,
      { name: ALGORITHM, length: KEY_LENGTH },
      false,
      ['encrypt', 'decrypt']
    );
  }

  // Encrypt text with a CryptoKey
  async encrypt(text, key) {
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const encodedText = this.encoder.encode(text);

    const ciphertext = await window.crypto.subtle.encrypt(
      { name: ALGORITHM, iv: iv },
      key,
      encodedText
    );

    return {
      ciphertext: this.arrayBufferToBase64(ciphertext),
      iv: this.arrayBufferToBase64(iv),
    };
  }

  // Decrypt text with a CryptoKey
  async decrypt(ciphertextBase64, ivBase64, key) {
    const ciphertext = this.base64ToArrayBuffer(ciphertextBase64);
    const iv = this.base64ToArrayBuffer(ivBase64);

    const decrypted = await window.crypto.subtle.decrypt(
      { name: ALGORITHM, iv: iv },
      key,
      ciphertext
    );

    return this.decoder.decode(decrypted);
  }

  // ArrayBuffer <-> Base64 helpers
  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  base64ToArrayBuffer(base64) {
    const binary = window.atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }
}

export const encryption = new EncryptionService();

// In-memory cache so we don't re-run PBKDF2 on every call (100k iterations).
// Cache is keyed by email so switching accounts within the same tab is safe.
let _cachedKey = null;
let _cachedKeyEmail = null;

// Persistent key manager: derives the key from the user email stored in
// localStorage. Survives tab close / browser restart (as long as the JWT
// is still valid and the email remains in localStorage).
export const sessionKeyManager = {
  // Store the user's email for future key derivation
  setUserEmail(email) {
    if (!email) return;
    const normalized = String(email).trim().toLowerCase();
    localStorage.setItem(USER_EMAIL_STORAGE_KEY, normalized);
    // Invalidate cache if email changed
    if (_cachedKeyEmail && _cachedKeyEmail !== normalized) {
      _cachedKey = null;
      _cachedKeyEmail = null;
    }
  },

  getUserEmail() {
    return localStorage.getItem(USER_EMAIL_STORAGE_KEY);
  },

  async getKey() {
    const email = this.getUserEmail();
    if (!email) {
      throw new Error(
        'No user email found for encryption key derivation. Please log in again.'
      );
    }

    if (_cachedKey && _cachedKeyEmail === email) {
      return _cachedKey;
    }

    const key = await encryption.deriveKey(email);
    _cachedKey = key;
    _cachedKeyEmail = email;
    return key;
  },

  // Alias kept for backward compatibility with existing callers.
  async getOrCreateKey() {
    return this.getKey();
  },

  clearKey() {
    _cachedKey = null;
    _cachedKeyEmail = null;
    localStorage.removeItem(USER_EMAIL_STORAGE_KEY);
    // Also clear legacy sessionStorage artifacts from prior versions
    try {
      sessionStorage.removeItem('selah_encryption_key');
      sessionStorage.removeItem('user_email');
    } catch (_) {
      /* ignore */
    }
  },
};

export default encryption;
